"use client";
import {
  ButtonTypes,
  ComponentProps,
  MediaVideo,
  RichText,
} from "@/baseComponents";
import { Button } from "@/components/atoms/button";
import { MediaImage } from "@/components/atoms/media";
import { MediaDataTypes } from "@/components/atoms/media/MediaData";
import MediaVideoEmbed from "@/components/atoms/media/MediaVideoEmbed";
import { CustomGrid } from "@/components/layout/grid/CustomGrid";
import { Card } from "@/components/molecules/card";
import {
  ButtonSizes,
  ButtonVariants,
  CardTypes,
  Cultures,
  MediaTypes,
} from "@/types/enums";
import { buildImageUrl } from "@/utils/imageSharpUrlBuilder";
import { GetUmbracoBaseUrl } from "@/utils/urlHelpers";
import {
  Box,
  chakra,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
} from "@chakra-ui/react";
import DOMPurify from "isomorphic-dompurify";
import { useTranslations } from "next-intl";

export interface DetailInfo {
  publicationDate?: string;
  hidePublicationDate?: boolean;
  type?: string;
  location?: string;
  surface?: string;
  status?: string;
}
export interface HeadingProps extends ComponentProps {
  title: string;
  subtitle?: string;
  button: any;
  smallHeader?: boolean;
  mediaData?: MediaDataTypes;
  detailInfo?: DetailInfo;
  culture: Cultures;
}

/**
 * Represents a Introduction component
 * @param richTextProps - Props for rendering a title with richtext.
 * @param buttonProps - Props for rendering a button
 * @returns Introduction
 */
export default chakra(HeadingComponent);
function HeadingComponent({
  title,
  subtitle,
  button,
  mediaData,
  detailInfo,
  smallHeader,
  culture,
  theme = "brand",
}: HeadingProps) {
  const t = useTranslations();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(culture, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const renderMedia = (mediaData: MediaDataTypes) => {
    switch (mediaData.type) {
      case MediaTypes.Video:
        return <MediaVideo zIndex={2} {...mediaData} />;
      case MediaTypes.Image:
        return (
          <MediaImage
            {...mediaData}
            hasBorderRadius={true}
            viewportWidthFactor={{ base: 0.5, md: 0.6, lg: 1 }}
            cropSizeAlias={{ base: "landscape" }}
            imageUrlBuilder={buildImageUrl}
            borderBottomRadius={0}
            borderTopLeftRadius={0}
            maxHeight={"33.75rem"}
          />
        );
      case MediaTypes.Embed:
        return <MediaVideoEmbed zIndex={2} {...mediaData} />;
    }
  };

  return (
    <Box position="relative">
      <CustomGrid
        gap={smallHeader ? 0 : 6}
        pb={smallHeader ? 0 : { base: 6, md: 9 }}
      >
        <GridItem colSpan={12}>
          <Heading
            as={smallHeader ? "h2" : "h1"}
            fontSize={
              smallHeader
                ? { base: "2xl", md: "2rem", lg: "5xl" }
                : { base: "2rem", lg: "5.5rem" }
            }
            lineHeight={1}
            fontWeight={400}
            color={"brand.aubergine"}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(title?.replace(/<\/p>/g, "</br>"), {
                ALLOWED_TAGS: ["strong", "br"],
              }),
            }}
            sx={{
              strong: {
                fontWeight: 400,
                color: "brand.cyan",
                fontFamily: "Lausanne, sans-serif",
              },
            }}
          />
        </GridItem>
        {detailInfo?.publicationDate && !detailInfo.hidePublicationDate && (
          <GridItem colSpan={12}>
            <Text fontSize={"md"} fontWeight={300} paddingTop={5}>
              {formatDate(detailInfo.publicationDate)}
            </Text>
          </GridItem>
        )}
        <GridItem colSpan={12}>
          <Flex
            justifyContent="space-between"
            alignContent="start"
            direction={{ base: "column", md: "row" }}
            gap={5}
          >
            {subtitle && (
              <RichText
                html={DOMPurify.sanitize(subtitle)}
                fontSize={"md"}
                fontWeight={300}
              />
            )}
            {button && (
              <Button
                text={button.title}
                url={ (button.linkType == "Media" ?  GetUmbracoBaseUrl() : "" ) + (button.route?.path || button.url)}
                buttonSize={ButtonSizes.Small}
                buttonType={ButtonTypes.TextOnly}
                buttonVariant={ButtonVariants.Primary}
                buttonProps={{ height: "100%" }}
                target={button.target}
              />
            )}
          </Flex>
        </GridItem>
        {detailInfo && (
          <GridItem colSpan={12}>
            <Grid
              templateColumns="repeat(12, 1fr)"
              gap={{ base: "0", md: "2.5rem" }}
            >
              {detailInfo.type && (
                <GridItem colSpan={{ base: 12, md: 6, xl: 3 }}>
                  <Card
                    cardType={CardTypes.Header}
                    title={detailInfo.type}
                    tag={t("type")}
                  />
                </GridItem>
              )}
              {detailInfo.location && (
                <GridItem colSpan={{ base: 12, md: 6, xl: 3 }}>
                  <Card
                    cardType={CardTypes.Header}
                    title={detailInfo.location}
                    tag={t("location")}
                  />
                </GridItem>
              )}
              {detailInfo.surface && (
                <GridItem colSpan={{ base: 12, md: 6, xl: 3 }}>
                  <Card
                    cardType={CardTypes.Header}
                    title={detailInfo.surface}
                    tag={t("rentableFloorArea")}
                  />
                </GridItem>
              )}
              {detailInfo.status && (
                <GridItem colSpan={{ base: 12, md: 6, xl: 3 }}>
                  <Card
                    cardType={CardTypes.Header}
                    title={detailInfo.status}
                    tag={t("status")}
                  />
                </GridItem>
              )}
            </Grid>
          </GridItem>
        )}
      </CustomGrid>
      {mediaData && (
        <Box pr={{ base: "1rem", md: 9, lg: 10 }} pb={{ base: 6, md: 9 }}>
          {renderMedia(mediaData)}
          <Box>
            <Box
              height={{ base: 8, md: 16 }}
              backgroundColor={`${theme}.aubergine`}
            />
            <Box
              height={{ base: 8, md: 16 }}
              backgroundColor={`${theme}.cyan`}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
